// ActiveWorkoutPill — Floating indicator shown when a workout session is active
// and the user has navigated away from /client/training.
//
// Shows: pulsing dot + workout name + elapsed time. Tap to return to the session.
// If there are unsynced offline mutations, shows an orange badge count.

import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import { Dumbbell, Wifi, WifiOff } from 'lucide-react';
import {
  loadActiveSession,
  loadWorkoutMeta,
  getMutationQueueSize,
} from '@/pages/client/training/sessionPersistence';

function formatElapsed(ms: number): string {
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}:${String(remainMins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

export function ActiveWorkoutPill() {
  const [location, navigate] = useLocation();
  const [hasSession, setHasSession] = useState(false);
  const [workoutName, setWorkoutName] = useState('Workout');
  const [sessionStart, setSessionStart] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  // Check for active session on mount + periodically
  useEffect(() => {
    const check = () => {
      const session = loadActiveSession();
      const meta = loadWorkoutMeta();
      const pending = getMutationQueueSize();

      if (session && session.mode !== 'select') {
        setHasSession(true);
        setWorkoutName(meta?.workoutName ?? 'Workout');
        setSessionStart(meta?.sessionStart ?? Date.now());
      } else {
        setHasSession(false);
      }
      setPendingCount(pending);
    };

    check();

    // Check every 2 seconds in case session state changes
    const interval = setInterval(check, 2000);

    // Listen for storage events (cross-tab)
    const handleStorage = (e: StorageEvent) => {
      if (e.key?.startsWith('workout-session:')) check();
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Tick the elapsed timer every second
  useEffect(() => {
    if (!hasSession || !sessionStart) return;
    const tick = () => setElapsed(Date.now() - sessionStart);
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [hasSession, sessionStart]);

  // Do not cover training controls or any messaging composer. The workout can
  // still be resumed from Training while the user is in a conversation.
  // A coach trains at /coach/my/training (the coach-as-athlete shell).
  const trainingPath = location.startsWith('/coach/') ? '/coach/my/training' : '/client/training';
  const isOnTrainingPage = /^\/(client|coach\/my)\/training(\/|$)/.test(location);
  const isOnMessagingPage = location.startsWith('/coach/inbox') || location.startsWith('/client/comms');
  const shouldShow = hasSession && !isOnTrainingPage && !isOnMessagingPage;

  if (!shouldShow) return null;

  return (
    <button
      data-active-workout-pill
      onClick={() => navigate(trainingPath)}
      className="fixed z-[45] left-4 right-4 flex items-center gap-3 px-4 py-3 rounded-2xl transition-all active:scale-[0.98] press-scale animate-in slide-in-from-bottom-4 duration-300 md:left-auto md:right-6 md:w-[min(420px,calc(100vw-48px))]"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
        background: 'linear-gradient(135deg, rgba(26,21,16,0.95) 0%, rgba(14,14,14,0.95) 100%)',
        border: '1px solid rgba(245,158,11,0.35)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(245,158,11,0.1) inset',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      {/* Pulsing dot */}
      <div className="relative flex-shrink-0">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(245,158,11,0.15)' }}
        >
          <Dumbbell className="w-5 h-5" style={{ color: '#f59e0b' }} />
        </div>
        <span
          className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full animate-pulse"
          style={{ background: '#10b981', boxShadow: '0 0 8px rgba(16,185,129,0.6)' }}
        />
      </div>

      {/* Workout info */}
      <div className="flex-1 min-w-0 text-left">
        <p
          className="text-sm font-black uppercase leading-tight truncate"
          style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#fff', letterSpacing: '0.04em' }}
        >
          {workoutName}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className="text-xs font-mono tabular-nums"
            style={{ color: 'rgba(245,158,11,0.8)' }}
          >
            {formatElapsed(elapsed)}
          </span>
          <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.72)' }}>
            In Progress
          </span>
        </div>
      </div>

      {/* Unsynced badge */}
      {pendingCount > 0 && (
        <div
          className="flex items-center gap-1 px-2 py-1 rounded-lg flex-shrink-0"
          style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)' }}
        >
          <WifiOff className="w-3 h-3" style={{ color: '#f97316' }} />
          <span className="text-[10px] font-bold" style={{ color: '#f97316' }}>
            {pendingCount}
          </span>
        </div>
      )}

      {/* Resume arrow */}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: 'var(--primary)', color: '#0e0e0e' }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7h8M8 3.5L11.5 7 8 10.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </button>
  );
}
